import React from 'react';
import { Form, Button } from 'react-bootstrap';
import { Trash2, Info } from 'lucide-react';
import FolderIcon from '@mui/icons-material/Folder';
import LaptopIcon from '@mui/icons-material/Laptop';
import StorageIcon from '@mui/icons-material/Storage';
import { ListItem, DragInfo } from '../../../types/files';
import { formatDate, formatFileSize } from '../utils/formatters';
import FileIcon from './FileIcon';

interface FileItemProps {
    item: ListItem;
    isSelected: boolean;
    isLoading: boolean;
    isAdmin: boolean;
    dragInfo: DragInfo;
    onSelect: (itemId: string, event: React.MouseEvent<HTMLElement> | React.ChangeEvent<HTMLInputElement>) => void;
    onDragStart: (e: React.DragEvent<HTMLTableRowElement>, itemId: string) => void;
    onDragOver: (e: React.DragEvent<HTMLTableRowElement>, itemId: string | null) => void;
    onDrop: (e: React.DragEvent<HTMLTableRowElement>, targetId: string | null) => Promise<void>;
    onFolderClick: (folderId: string, folderName: string) => void;
    onContextMenu: (
        e: React.MouseEvent<HTMLDivElement>,
        id: string,
        name: string,
        type: 'folder' | 'file'
    ) => void;
    onDragEnd?: (e: React.DragEvent<HTMLTableRowElement>) => void;
}

export const FileItem: React.FC<FileItemProps> = ({
                                                      item,
                                                      isSelected,
                                                      isLoading,
                                                      isAdmin,
                                                      dragInfo,
                                                      onSelect,
                                                      onDragStart,
                                                      onDragOver,
                                                      onDrop,
                                                      onFolderClick,
                                                      onContextMenu,
                                                      onDragEnd,
                                                  }) => {
    const handleRowClick = (e: React.MouseEvent<HTMLTableRowElement>) => {
        const target = e.target as HTMLElement;
        if (!target.closest('.checkbox-column') && !target.closest('.actions-column')) {
            if (item.type === 'folder') {
                onFolderClick(item.id, item.name);
            } else {
                onSelect(item.id, e);
            }
        }
    };

    const getFileSize = (item: ListItem): number => {
        if (item.type === 'folder' && item.folder_metadata?.recursive_size) {
            return item.folder_metadata.recursive_size;
        }
        return item.metadata?.file_size || 0;
    };

    // The issue is that the metadata in the item has moved to folder_metadata
// after your backend changes. Let's update renderFolderMetadata:

    const renderFolderMetadata = (item: ListItem) => {
        if (item.type !== 'folder') return null;

        // Log full item for debugging
        console.log('Processing folder:', item.name, {
            metadata: item.metadata,
            file_count: item.file_count,
            raw_item: item
        });

        // Get counts from the correct metadata fields based on available data
        const totalFiles = String(Number(item.metadata?.file_count || item.file_count || 0));
        const directFiles = String(Number(item.metadata?.direct_file_count || 0));
        const totalSubfolders = String(Number(item.metadata?.total_subfolder_count || 0));
        const directSubfolders = String(Number(item.metadata?.child_count || 0));
        const totalSize = Number(item.metadata?.total_size || 0);

        // Build the statistics text
        let statsText = `${totalFiles} total files (${directFiles} direct)`;

        if (Number(totalSubfolders) > 0) {
            statsText += `, ${totalSubfolders} total subfolders (${directSubfolders} direct)`;
        }

        if (totalSize) {
            statsText += ` • ${formatFileSize(totalSize)}`;
        }

        return (
            <small className="text-muted">
                {statsText}
                {' • Created by '}
                {item.created_by_name || 'Unknown'}
            </small>
        );
    };

    const handleContextMenu = (e: React.MouseEvent<HTMLDivElement>) => {
        e.preventDefault();
        onContextMenu(e, item.id, item.name, item.type);
    };

    const handleDragEnd = (e: React.DragEvent<HTMLTableRowElement>) => {
        e.preventDefault();
        e.stopPropagation();
        if (onDragEnd) {
            onDragEnd(e);
        }
    };

    return (
        <tr
            draggable
            onDragStart={(e) => onDragStart(e, item.id)}
            onDragOver={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onDragOver(e, item.id);
            }}
            onDrop={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onDrop(e, item.id);
            }}
            onDragEnd={handleDragEnd}
            onClick={handleRowClick}
            onContextMenu={handleContextMenu}
            style={{
                cursor: 'pointer',
                opacity: dragInfo.draggedItemId === item.id ? '0.5' : '1',
                backgroundColor: dragInfo.dragOverItemId === item.id ? 'var(--bs-table-hover-bg)' : undefined,
            }}
            className={isSelected ? 'table-active' : ''}
        >
            {isAdmin && (
                <td className="checkbox-column" onClick={e => e.stopPropagation()}>
                    <Form.Check
                        type="checkbox"
                        checked={isSelected}
                        onChange={(e) => onSelect(item.id, e)}
                        disabled={isLoading}
                    />
                </td>
            )}
            <td>
                <div className="d-flex align-items-center">
                    {item.type === 'folder' ? (
                        <div className="d-flex align-items-center">
                            <FolderIcon
                                sx={{
                                    width: 20,
                                    height: 20,
                                    color: 'primary.main',
                                    marginRight: '0.5rem'
                                }}
                            />
                            <div>
                                <div>{item.name}</div>
                                {renderFolderMetadata(item)}
                            </div>
                        </div>
                    ) : (
                        <div className="d-flex align-items-center">
                            <FileIcon
                                mimeType={item.metadata?.mime_type || item.name || ''}
                                className="me-2"
                                size={20}
                            />
                            <div>
                                <div>{item.name}</div>
                                <small className="text-muted">
                                    Created by {item.created_by_name || 'Unknown'}
                                </small>
                            </div>
                        </div>
                    )}
                </div>
            </td>
            <td>
                {item.type === 'folder' ?
                    (item.metadata?.total_size ? formatFileSize(item.metadata.total_size) : '-') :
                    formatFileSize(getFileSize(item))}
            </td>
            <td>{formatDate(item.updated_at || item.created_at)}</td>
            <td>
                {item.type === 'folder' ? (
                    ''
                ) : (
                    <div className="d-flex align-items-center">
                        {item.source_type === 'drive_sync' ? (
                            <StorageIcon sx={{width: 16, height: 16, color: 'action.active'}}/>
                        ) : (
                            <LaptopIcon sx={{width: 16, height: 16, color: 'action.active'}}/>
                        )}
                        {item.status === 'failed' && (
                            <span className="badge bg-danger ms-2">Failed</span>
                        )}
                        {item.status === 'pending' && (
                            <span className="badge bg-warning ms-2">Processing</span>
                        )}
                    </div>
                )}
            </td>
            <td className="actions-column">
                <div className="d-flex gap-2">
                    {isAdmin && (
                        <Button
                            variant="outline-danger"
                            size="sm"
                            title="Delete"
                            onClick={(e) => {
                                e.stopPropagation();
                                const mockEvent = new MouseEvent('contextmenu', {
                                    bubbles: true,
                                    cancelable: true,
                                }) as any;
                                onContextMenu(mockEvent, item.id, item.name, item.type);
                            }}
                            disabled={isLoading}
                        >
                            <Trash2 size={16}/>
                        </Button>
                    )}
                    <Button
                        variant="outline-secondary"
                        size="sm"
                        title="Details"
                        onClick={(e) => {
                            e.stopPropagation();
                        }}
                        disabled={isLoading}
                    >
                        <Info size={16}/>
                    </Button>
                </div>
            </td>
        </tr>
    );
};